import * as React from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { ArrowLeft } from "lucide-react"
import { useIntl } from "react-intl"
import { AppShell } from "@/components/AppShell"
import { AskAssistantButton } from "@/components/assistant/AskAssistantButton"
import { HelpfulPrompt } from "@/components/quizzes/HelpfulPrompt"
import { ChoiceOption } from "@/components/quizzes/ChoiceOption"
import { CHOICE_LETTERS } from "@/components/quizzes/format"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import type { PageProps } from "@/types/inertia"
import type { TakeQuestion } from "@/types/quizzes"

export default function QuizTake({
  attempt,
  quiz,
  questions,
}: {
  attempt: { id: number; retry: boolean; finished: boolean }
  quiz: { id: number; title: string }
  questions: TakeQuestion[]
}) {
  const intl = useIntl()
  const { props } = usePage<PageProps>()
  const firstUnanswered = questions.findIndex((q) => !q.answer)
  const [index, setIndex] = React.useState(firstUnanswered === -1 ? questions.length - 1 : firstUnanswered)
  const [selected, setSelected] = React.useState<number | null>(null)
  const [processing, setProcessing] = React.useState(false)

  const question = questions[index]
  const answer = question?.answer
  const isLast = index === questions.length - 1
  const answeredCount = questions.filter((q) => q.answer).length
  const title = attempt.retry ? intl.formatMessage({ id: "quizzes.retry_title" }, { title: quiz.title }) : quiz.title
  const description = intl.formatMessage({ id: "quizzes.take_meta" }, { title: quiz.title })

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (selected === null || answer) return
    router.post(
      `/attempts/${attempt.id}/answers`,
      { question_id: question.id, choice: selected },
      {
        preserveScroll: true,
        preserveState: true,
        onStart: () => setProcessing(true),
        onFinish: () => setProcessing(false),
      },
    )
  }

  const next = () => {
    setIndex((i) => i + 1)
    setSelected(null)
  }

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell>
        <Link href={`/quizzes/${quiz.id}`} className="mb-4 inline-flex items-center gap-1 text-sm">
          <ArrowLeft className="h-4 w-4" /> {quiz.title}
        </Link>

        <div className="mx-auto max-w-2xl">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h1>{quiz.title}</h1>
            {attempt.retry && <Badge tone="muted">{intl.formatMessage({ id: "quizzes.retry_badge" })}</Badge>}
          </div>

          {question ? (
            <>
              <div className="mt-4 flex items-center gap-3">
                <div
                  className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface"
                  role="progressbar"
                  aria-label={intl.formatMessage({ id: "quizzes.answered_label" })}
                  aria-valuemin={0}
                  aria-valuemax={questions.length}
                  aria-valuenow={answeredCount}
                >
                  <div
                    className="h-full bg-accent transition-[width]"
                    style={{ width: `${(answeredCount / questions.length) * 100}%` }}
                  />
                </div>
                <span className="text-sm text-ink-muted">
                  {intl.formatMessage({ id: "quizzes.question_x_of_y" }, { current: index + 1, total: questions.length })}
                </span>
              </div>

              <form onSubmit={submit} className="mt-6">
                {question.content_area_name && (
                  <Badge tone="muted" className="mb-3">
                    {question.content_area_name}
                  </Badge>
                )}
                <fieldset key={question.id}>
                  <legend className="whitespace-pre-wrap break-words">{question.text}</legend>
                  <div className="mt-4 space-y-2">
                    {question.choices.map((choice, i) => (
                      <ChoiceOption
                        key={i}
                        name={`question-${question.id}`}
                        index={i}
                        text={choice}
                        checked={answer ? answer.chosen_choice === i : selected === i}
                        disabled={!!answer || processing}
                        state={
                          !answer
                            ? null
                            : i === answer.correct_choice
                              ? "correct"
                              : i === answer.chosen_choice
                                ? "wrong"
                                : null
                        }
                        onSelect={() => setSelected(i)}
                      />
                    ))}
                  </div>
                </fieldset>

                {answer && (
                  <Feedback answer={answer} choices={question.choices}>
                    <div className="space-y-3">
                      <HelpfulPrompt key={question.id} attemptId={attempt.id} questionId={question.id} />
                      <AskAssistantButton attemptId={attempt.id} questionId={question.id} />
                    </div>
                  </Feedback>
                )}

                {props.errors?.choice && !answer && (
                  <p className="mt-3 text-sm text-danger-display">{props.errors.choice}</p>
                )}

                <div className="mt-6 flex justify-end">
                  {!answer ? (
                    <Button type="submit" disabled={selected === null || processing}>
                      {intl.formatMessage({ id: "quizzes.check_answer" })}
                    </Button>
                  ) : isLast || answeredCount === questions.length ? (
                    <Button asChild>
                      <Link href={`/attempts/${attempt.id}/results`} autoFocus>
                        {intl.formatMessage({ id: "quizzes.see_results" })}
                      </Link>
                    </Button>
                  ) : (
                    <Button type="button" onClick={next} autoFocus>
                      {intl.formatMessage({ id: "quizzes.next_question" })}
                    </Button>
                  )}
                </div>
              </form>
            </>
          ) : (
            <div className="callout mt-6">
              <p>{intl.formatMessage({ id: "quizzes.no_questions_left" })}</p>
            </div>
          )}
        </div>
      </AppShell>
    </>
  )
}

function Feedback({
  answer,
  choices,
  children,
}: {
  answer: NonNullable<TakeQuestion["answer"]>
  choices: string[]
  children?: React.ReactNode
}) {
  const intl = useIntl()
  return (
    <div className={cn("callout mt-6", answer.correct ? "callout-accent" : "callout-danger")} role="status">
      <p>
        <strong>{answer.correct ? intl.formatMessage({ id: "quizzes.correct" }) : intl.formatMessage({ id: "quizzes.not_quite" })}</strong>{" "}
        {!answer.correct &&
          intl.formatMessage({ id: "quizzes.correct_is" }, { letter: CHOICE_LETTERS[answer.correct_choice], choice: choices[answer.correct_choice] })}
      </p>
      {answer.explanation && <p className="mt-2 whitespace-pre-wrap">{answer.explanation}</p>}
      {children && <div className="mt-3">{children}</div>}
    </div>
  )
}
